// Отображение рисунка: изображение в реальном масштабе листа (см), ручки изменения ширины с подсказкой
// «12,5 см», быстрые размеры, поворот, обрезка, замена, замещающий текст, предупреждения (разрешение, размер),
// превью во время загрузки; подпись «Рисунок N. …» редактируется прямо под изображением.
import { useEffect, useRef, useState } from 'react';
import { NodeViewContent, NodeViewWrapper, type NodeViewProps } from '@tiptap/react';
import { NodeSelection } from '@tiptap/pm/state';
import { assetUrl } from '../../../core/schema/objects';
import { ctx } from '../../../core/ctx';
import { appStore } from '../../../core/store';
import { Btn } from '../../../ui/controls';
import { Icon } from '../../../ui/icons';
import { cmToWidth, effectiveCm, fmtCm, naturalCm, PX_PER_CM, TEXT_W_CM, MAX_H_CM, widthToCm } from '../util';
import { assetInfo, pendingOf, usePending, useAssetInfo } from './upload';
import { partSize, planParts, type PartImage } from './parts';

/** Курсор внутри узла (в подписи) или узел выделен. */
function useInside(editor: NodeViewProps['editor'], getPos: NodeViewProps['getPos'], size: number): boolean {
  const [inside, setInside] = useState(false);
  useEffect(() => {
    const upd = () => {
      const p = typeof getPos === 'function' ? getPos() : undefined;
      if (typeof p !== 'number') return setInside(false);
      const { from, to } = editor.state.selection;
      setInside(editor.isFocused && from >= p && to <= p + size);
    };
    upd();
    editor.on('selectionUpdate', upd);
    editor.on('focus', upd);
    editor.on('blur', upd);
    return () => { editor.off('selectionUpdate', upd); editor.off('focus', upd); editor.off('blur', upd); };
  }, [editor, getPos, size]);
  return inside;
}

/** Почему нет изображения (импорт .docx, attrs.missing) - текст в пустом рисунке. */
const MISSING_TEXT: Record<string, string> = {
  chart: 'Диаграмма Word не перенесена',
  shapes: 'Рисунок из фигур Word не перенесён',
  smartart: 'Схема SmartArt не перенесена',
  ink: 'Рукописный рисунок не перенесён',
  ole: 'Встроенный объект не перенесён',
  'no-image': 'В исходном файле у этой подписи нет изображения',
};

/** Текст строчных узлов (пояснения рисунка attrs.expl). */
function inlineText(nodes: any[] | null | undefined): string {
  return (nodes || []).map((n: any) => (n?.type === 'text' ? n.text || '' : n?.type === 'mathInline' ? (n.attrs?.latex || '') : inlineText(n?.content))).join('');
}

export function FigureView({ node, editor, getPos, selected, updateAttributes }: NodeViewProps) {
  const { src, width, alt, id } = node.attrs as { src: string; width: string | null; alt: string; id: string };
  const missing = node.attrs.missing as string | null;
  const parts = (Array.isArray(node.attrs.images) ? node.attrs.images : []) as PartImage[];
  const composite = parts.length > 1;
  const expl = inlineText(node.attrs.expl as any[] | null);
  const pend = usePending(id);
  const info = useAssetInfo(src);
  const imgRef = useRef<HTMLImageElement>(null);
  const [nat, setNat] = useState<[number, number]>([0, 0]);
  const [drag, setDrag] = useState<number | null>(null);      // ширина в см во время перетаскивания ручки
  const inside = useInside(editor, getPos, node.nodeSize);
  const active = selected || inside;

  const wpx = info?.width || nat[0];
  const hpx = info?.height || nat[1];
  const dpi = info?.dpi ?? 96;
  const cm = drag ?? (wpx ? effectiveCm(width, wpx, hpx, dpi) : null);
  const natCm = wpx ? Math.min(naturalCm(wpx, dpi), TEXT_W_CM) : null;
  const maxCm = wpx && hpx ? Math.min(TEXT_W_CM, (MAX_H_CM * wpx) / hpx) : TEXT_W_CM;
  const hCm = cm && wpx ? (cm * hpx) / wpx : null;
  const effDpi = cm && wpx ? Math.round(wpx / (cm / 2.54)) : null;
  const isJpeg = /\.jpe?g$/i.test(src);
  const warns: string[] = [];                                     // у составного размеры частей задаёт раскладка
  if (!composite && effDpi && isJpeg && effDpi < 150) warns.push(`≈${effDpi} dpi при этом размере: для сканов и фото печатных материалов нужно не менее 150 dpi (4.5.2) - уменьшите рисунок или возьмите изображение лучше`);
  else if (!composite && effDpi && effDpi < 72) warns.push(`≈${effDpi} dpi: изображение растянуто больше исходного и будет размытым`);
  const wAttr = widthToCm(width);
  if (!composite && wAttr && wAttr > TEXT_W_CM + 0.01) warns.push('Шире поля набора 17 см - при экспорте будет уменьшен');

  const select = () => {
    const p = typeof getPos === 'function' ? getPos() : undefined;
    if (typeof p === 'number') editor.view.dispatch(editor.state.tr.setSelection(NodeSelection.create(editor.state.doc, p)));
    editor.view.focus();
  };

  // --- ручки ширины: тянем правую/левую - ширина меняется симметрично (рисунок по центру)
  const startResize = (e: React.PointerEvent, side: 1 | -1) => {
    if (!cm || !wpx) return;
    e.preventDefault(); e.stopPropagation();
    const zoom = appStore.getState().zoom || 1;
    const x0 = e.clientX; const w0 = cm;
    let cur = w0;
    const move = (ev: PointerEvent) => {
      let w = w0 + ((ev.clientX - x0) * side * 2) / zoom / PX_PER_CM;
      if (!ev.altKey) {
        w = Math.round(w * 2) / 2;                                         // шаг 0,5 см
        if (natCm && Math.abs(w - natCm) < 0.35) w = natCm;               // «прилипание» к исходному размеру
        if (Math.abs(w - maxCm) < 0.35) w = maxCm;                        // и к ширине поля
      }
      cur = Math.max(1.5, Math.min(maxCm, w));
      setDrag(cur);
    };
    const up = () => {
      window.removeEventListener('pointermove', move, true);
      window.removeEventListener('pointerup', up, true);
      setDrag(null);
      if (Math.abs(cur - w0) > 0.04) updateAttributes({ width: cmToWidth(cur) });
    };
    window.addEventListener('pointermove', move, true);
    window.addEventListener('pointerup', up, true);
  };

  const run = (cmd: string, arg?: unknown) => { select(); setTimeout(() => ctx.run(cmd, arg), 0); };
  const imgUrl = pend?.url || (src ? assetUrl(src) : '');
  // составной рисунок: части «а», «б» … строками, как в отчёте (parts.ts = writer emit_parts)
  const partSrc = (k: number) => (k === 0 ? src || parts[0]?.src || '' : parts[k]?.src || '');
  const plan = composite
    ? planParts(parts.map((pt, k) => (partSrc(k) ? partSize(assetInfo(partSrc(k)), pt.width) : null)),
      node.attrs.layout as string | null, node.attrs.cols as number | null, parts.some((pt) => !!pt.label))
    : null;
  const partView = (k: number) => {
    const pt = parts[k];
    const pp = pendingOf(`${id}#${k}`);
    const url = pp?.url || (partSrc(k) ? assetUrl(partSrc(k)) : '');
    const w = plan!.widths[k];
    const name = pt.label ? `«${pt.label}»` : String(k + 1);
    return (
      <span key={k} className="tfc-fig-part" data-testid="figure-part">
        {url ? (
          <span className="tfc-img-wrap" style={{ width: `${w}cm` }}>
            <img src={url} alt="" draggable={false} style={{ width: `${w}cm` }} />
            {pp && <span className={'tfc-fig-pending' + (pp.error ? ' is-error' : '')}><Icon name="spinner" size={16} className="spin" /> {pp.progress}</span>}
            {active && !pp && (
              <button type="button" className="tfc-fig-part-btn" title={`Заменить изображение части ${name}`} onClick={() => run('figure.replacePart', k)}>
                <Icon name="tfc-replace" size={14} />
              </button>
            )}
          </span>
        ) : (
          <span className="tfc-fig-empty is-part" style={{ width: `${w}cm`, minHeight: `${plan!.heights[k]}cm` }} data-testid="figure-part-empty">
            <Icon name="image" size={22} />
            <span>Часть {name}: нет изображения</span>
            <Btn label="Выбрать файл…" onClick={() => run('figure.replacePart', k)} testid="figure-part-upload" />
          </span>
        )}
        {pt.label ? <span className="tfc-fig-part-label">{pt.label}</span> : null}
      </span>
    );
  };

  return (
    <NodeViewWrapper as="figure" className={'sto-figure tfc-figure' + (active ? ' is-active' : '') + (selected ? ' is-selected' : '')} data-type="figure">
      <div className="sto-figure-img tfc-fig-box" contentEditable={false} data-drag-handle="" draggable={!pend && !!src}
        onMouseDown={(e) => { if ((e.target as HTMLElement).closest('.tfc-fig-tools, .tfc-handle, button')) return; if (e.button === 0) select(); }}
        onDoubleClick={() => run('figure.props')}>
        {plan ? (
          <span className="tfc-fig-grid" data-testid="figure-parts" data-cols={plan.cols}>
            {plan.rows.map((row, r) => <span key={r} className="tfc-fig-row">{row.map(partView)}</span>)}
          </span>
        ) : imgUrl ? (
          <span className="tfc-img-wrap" style={cm ? { width: `${cm}cm` } : undefined}>
            <img ref={imgRef} src={imgUrl} alt={alt || ''} draggable={false} data-testid="figure-img"
              style={cm ? { width: `${cm}cm` } : { maxWidth: '17cm' }}
              onLoad={(e) => { const im = e.currentTarget; setNat([im.naturalWidth, im.naturalHeight]); }} />
            {pend && <span className={'tfc-fig-pending' + (pend.error ? ' is-error' : '')}><Icon name="spinner" size={16} className="spin" /> {pend.progress}</span>}
            {active && !pend && (
              <>
                <span className="tfc-handle tfc-handle-l" title="Потяните, чтобы изменить ширину (Alt - без привязки)" onPointerDown={(e) => startResize(e, -1)} />
                <span className="tfc-handle tfc-handle-r" title="Потяните, чтобы изменить ширину (Alt - без привязки)" onPointerDown={(e) => startResize(e, 1)} data-testid="figure-handle" />
              </>
            )}
            {(active || drag !== null) && cm && (
              <span className="tfc-fig-size" data-testid="figure-size">{fmtCm(cm)}{hCm ? ` × ${fmtCm(hCm)}` : ''}{!wAttr && drag === null ? ' (исходный)' : ''}</span>
            )}
          </span>
        ) : (
          <span className={'tfc-fig-empty' + (missing ? ' is-missing' : '')} data-testid="figure-missing">
            <Icon name="image" size={28} />
            <span>{missing ? `${MISSING_TEXT[missing] || 'Изображение не перенесено'} - вставьте изображение (в отчёте будет рамка «Рисунок не перенесён»)` : 'Нет изображения'}</span>
            {missing && alt ? <span className="tfc-fig-missing-alt">{alt}</span> : null}
            <Btn label="Выбрать файл…" onClick={() => run('figure.replace')} />
          </span>
        )}
        {expl && <div className="tfc-fig-expl" title="Пояснения к рисунку (над подписью, 4.5.7)">{expl}</div>}
        {active && !pend && composite && (
          <div className="tfc-fig-tools" data-testid="figure-tools" onMouseDown={(e) => e.preventDefault()}>
            <span className="tfc-fig-meta" title="Раскладка частей в отчёте: Свойства рисунка - ряд, столбец, сетка">{parts.length} {parts.length < 5 ? 'части' : 'частей'}</span>
            <span className="tfc-tb-sep" />
            <Btn icon="tfc-alt" title={alt ? `Замещающий текст: ${alt}` : 'Замещающий текст (для незрячих, 9.4.4)'} active={!!alt} onClick={() => run('figure.props', 'alt')} />
            <Btn icon="settings" title="Свойства рисунка…" onClick={() => run('figure.props')} testid="fig-props" />
          </div>
        )}
        {active && !pend && src && !composite && (
          <div className="tfc-fig-tools" data-testid="figure-tools" onMouseDown={(e) => e.preventDefault()}>
            <Btn icon="tfc-fit" title="По ширине поля набора (17 см)" onClick={() => run('figure.width', 'full')} testid="fig-w-full" />
            <Btn label="½" title="Половина ширины поля (8,5 см)" onClick={() => run('figure.width', 'half')} testid="fig-w-half" />
            <Btn label="1:1" title="Исходный размер (по разрешению файла)" onClick={() => run('figure.width', 'natural')} testid="fig-w-natural" />
            <span className="tfc-tb-sep" />
            <Btn icon="tfc-rotate-ccw" title="Повернуть влево" onClick={() => run('figure.rotate', -90)} />
            <Btn icon="tfc-rotate-cw" title="Повернуть вправо" onClick={() => run('figure.rotate', 90)} testid="fig-rotate" />
            <Btn icon="tfc-crop" title="Обрезать…" onClick={() => run('figure.crop')} testid="fig-crop" />
            <Btn icon="tfc-replace" title="Заменить изображение (подпись сохранится)" onClick={() => run('figure.replace')} />
            <span className="tfc-tb-sep" />
            <Btn icon="tfc-alt" title={alt ? `Замещающий текст: ${alt}` : 'Замещающий текст (для незрячих, 9.4.4)'} active={!!alt} onClick={() => run('figure.props', 'alt')} />
            <Btn icon="settings" title="Свойства рисунка…" onClick={() => run('figure.props')} testid="fig-props" />
          </div>
        )}
        {warns.length > 0 && active && <div className="tfc-fig-warn" title={warns.join('\n')}><Icon name="warn" size={14} /> {warns[0]}</div>}
      </div>
      <NodeViewContent className="sto-figure-cap" />
    </NodeViewWrapper>
  );
}

/** События мыши в области изображения ProseMirror не обрабатывает (щелчок - выделить рисунок целиком,
 *  ручки, кнопки); перетаскивание (drag*) и всё в подписи - обычным порядком. */
export function figureStopEvent({ event }: { event: Event }): boolean {
  const t = event.target as HTMLElement | null;
  if (!t || !t.closest) return false;
  if (event.type.startsWith('drag') || event.type === 'drop') return false;
  return !!t.closest('.tfc-fig-box');
}
